import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Brain, LogOut, Save, Mail, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contentRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/content`),
        axios.get(`${API}/submissions`, { withCredentials: true })
      ]);
      setContent(contentRes.data);
      setSubmissions(submissionsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const updateSectionContent = (sectionId, field, value) => {
    setContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.section_id === sectionId
          ? { ...section, content: { ...section.content, [field]: value } }
          : section
      )
    }));
  };

  const updateFeature = (sectionId, featureIdx, field, value) => {
    setContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.section_id === sectionId
          ? {
              ...section,
              content: {
                ...section.content,
                features: section.content.features.map((f, i) => 
                  i === featureIdx ? { ...f, [field]: value } : f
                )
              }
            }
          : section
      )
    }));
  };

  const updateService = (serviceIdx, field, value) => {
    setContent(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.section_id === 'services'
          ? {
              ...section,
              content: {
                ...section.content,
                services: section.content.services.map((s, i) => 
                  i === serviceIdx ? { ...s, [field]: value } : s
                )
              }
            }
          : section
      )
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/content`,
        { sections: content.sections },
        { withCredentials: true }
      );
      toast.success('Content saved successfully!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  const hero = content.sections.find(s => s.section_id === 'hero')?.content || {};
  const expertise = content.sections.find(s => s.section_id === 'expertise')?.content || {};
  const services = content.sections.find(s => s.section_id === 'services')?.content || {};
  const security = content.sections.find(s => s.section_id === 'security')?.content || {};
  const about = content.sections.find(s => s.section_id === 'about')?.content || {};
  const contact = content.sections.find(s => s.section_id === 'contact')?.content || {};

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-[#10B981]">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black text-[#0A0A0A]">Xceltrain LLC</h1>
              <p className="text-xs text-[#52525B]">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#52525B]">Welcome, {user?.username}</span>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="content" className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="content" data-testid="tab-content">
              <FileText className="w-4 h-4 mr-2" />
              Edit Content
            </TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions">
              <Mail className="w-4 h-4 mr-2" />
              Submissions ({submissions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="space-y-8">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#0A0A0A]">Edit Website Content</h2>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#10B981] hover:bg-[#059669] text-white"
                data-testid="save-content-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>

            {/* Hero Section */}
            <div className="bg-white p-6 rounded-md border border-[#E4E4E7]" data-testid="edit-hero-section">
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-4">Hero Section</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Headline</label>
                  <Textarea
                    value={hero.headline || ''}
                    onChange={(e) => updateSectionContent('hero', 'headline', e.target.value)}
                    rows={3}
                    data-testid="edit-hero-headline"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Subheadline</label>
                  <Textarea
                    value={hero.subheadline || ''}
                    onChange={(e) => updateSectionContent('hero', 'subheadline', e.target.value)}
                    rows={3}
                    data-testid="edit-hero-subheadline"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">CTA Button Text</label>
                  <Input
                    value={hero.cta_text || ''}
                    onChange={(e) => updateSectionContent('hero', 'cta_text', e.target.value)}
                    data-testid="edit-hero-cta"
                  />
                </div>
              </div>
            </div>

            {/* Expertise Section */}
            <div className="bg-white p-6 rounded-md border border-[#E4E4E7]" data-testid="edit-expertise-section">
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-4">Expertise Section</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Title</label>
                  <Input
                    value={expertise.title || ''}
                    onChange={(e) => updateSectionContent('expertise', 'title', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Subtitle</label>
                  <Input
                    value={expertise.subtitle || ''}
                    onChange={(e) => updateSectionContent('expertise', 'subtitle', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Description</label>
                  <Textarea
                    value={expertise.description || ''}
                    onChange={(e) => updateSectionContent('expertise', 'description', e.target.value)}
                    rows={4}
                  />
                </div>
                {expertise.features?.map((feature, idx) => (
                  <div key={idx} className="border-t pt-4 mt-4">
                    <h4 className="font-semibold mb-2">Feature {idx + 1}</h4>
                    <div className="space-y-3">
                      <Input
                        placeholder="Feature Title"
                        value={feature.title || ''}
                        onChange={(e) => updateFeature('expertise', idx, 'title', e.target.value)}
                      />
                      <Textarea
                        placeholder="Feature Description"
                        value={feature.description || ''}
                        onChange={(e) => updateFeature('expertise', idx, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Services Section */}
            <div className="bg-white p-6 rounded-md border border-[#E4E4E7]" data-testid="edit-services-section">
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-4">Services Section</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Title</label>
                  <Input
                    value={services.title || ''}
                    onChange={(e) => updateSectionContent('services', 'title', e.target.value)}
                  />
                </div>
                {services.services?.map((service, idx) => (
                  <div key={idx} className="border-t pt-4 mt-4">
                    <h4 className="font-semibold mb-2">Service {idx + 1}</h4>
                    <div className="space-y-3">
                      <Input
                        placeholder="Service Title"
                        value={service.title || ''}
                        onChange={(e) => updateService(idx, 'title', e.target.value)}
                      />
                      <Textarea
                        placeholder="Service Description"
                        value={service.description || ''}
                        onChange={(e) => updateService(idx, 'description', e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Section */}
            <div className="bg-white p-6 rounded-md border border-[#E4E4E7]" data-testid="edit-security-section">
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-4">Security & Compliance Section</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Title</label>
                  <Input
                    value={security.title || ''}
                    onChange={(e) => updateSectionContent('security', 'title', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Subtitle</label>
                  <Input
                    value={security.subtitle || ''}
                    onChange={(e) => updateSectionContent('security', 'subtitle', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Description</label>
                  <Textarea
                    value={security.description || ''}
                    onChange={(e) => updateSectionContent('security', 'description', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* About Section */}
            <div className="bg-white p-6 rounded-md border border-[#E4E4E7]" data-testid="edit-about-section">
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-4">About Us Section</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Title</label>
                  <Input
                    value={about.title || ''}
                    onChange={(e) => updateSectionContent('about', 'title', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Description</label>
                  <Textarea
                    value={about.description || ''}
                    onChange={(e) => updateSectionContent('about', 'description', e.target.value)}
                    rows={4}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Mission</label>
                  <Textarea
                    value={about.mission || ''}
                    onChange={(e) => updateSectionContent('about', 'mission', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="bg-white p-6 rounded-md border border-[#E4E4E7]" data-testid="edit-contact-section">
              <h3 className="text-xl font-bold text-[#0A0A0A] mb-4">Contact Section</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Title</label>
                  <Input
                    value={contact.title || ''}
                    onChange={(e) => updateSectionContent('contact', 'title', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Subtitle</label>
                  <Input
                    value={contact.subtitle || ''}
                    onChange={(e) => updateSectionContent('contact', 'subtitle', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="submissions">
            <h2 className="text-2xl font-bold text-[#0A0A0A] mb-6">Contact Form Submissions</h2>
            {submissions.length === 0 ? (
              <div className="bg-white p-12 rounded-md border border-[#E4E4E7] text-center">
                <Mail className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                <p className="text-[#52525B]">No submissions yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {submissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="bg-white p-6 rounded-md border border-[#E4E4E7]"
                    data-testid={`submission-${submission.id}`}
                  >
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-semibold text-[#52525B] mb-1">Name</p>
                        <p className="text-sm text-[#0A0A0A]">{submission.name}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#52525B] mb-1">Organization</p>
                        <p className="text-sm text-[#0A0A0A]">{submission.organization}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#52525B] mb-1">Jurisdiction</p>
                        <p className="text-sm text-[#0A0A0A]">{submission.jurisdiction}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#52525B] mb-1">Submitted</p>
                        <p className="text-sm text-[#0A0A0A]">{new Date(submission.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#52525B] mb-1">Message</p>
                      <p className="text-sm text-[#0A0A0A]">{submission.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};